import type { User } from '@obraplan/core/domain/access/user';
import type { AuthUserLookup } from './authentication';
import type {
  RoleAssignment,
  RoleWithPermissions,
} from '@obraplan/core/domain/access/permission-resolver';
import type { PermissionDefinition } from '@obraplan/core/domain/access/permissions';
import type { Role } from '@obraplan/core/domain/access/role';
import type { AuditLog } from '@obraplan/core/domain/audit/audit-log';
import type { Project } from '@obraplan/core/domain/structure/project';
import type {
  WorkCalendar,
  WorkCalendarException,
} from '@obraplan/core/domain/calendar/work-calendar';
import type {
  ProjectId,
  RoleAssignmentId,
  RoleId,
  UserId,
  WorkCalendarId,
} from '@obraplan/core/domain/shared/ids';

/**
 * Portas de persistência.
 *
 * O caso de uso depende destas interfaces, nunca do Dexie (ADR-005/101). É o que permite
 * testar a aplicação sem banco e o que tornaria a adição de um servidor uma
 * implementação, não uma reescrita.
 *
 * Regra sem exceção: toda consulta operacional recebe `projectId`. Não existe método que
 * devolva dados de todas as obras sem parâmetro explícito (RN-001).
 */

/**
 * Estende `AuthUserLookup` em vez de repetir `findByEmail`.
 *
 * A porta estreita nasceu para a entrada no sistema, que só precisa achar alguém pelo
 * endereço. Agora o cadastro também precisa — para recusar e-mail repetido antes de o
 * índice único do banco estourar com erro cru. Herdar mantém o caso de uso da entrada
 * dependendo só do que ele usa, e dá ao repositório concreto uma implementação só.
 */
export interface UserRepository extends AuthUserLookup {
  findById(id: UserId): Promise<User | undefined>;
  findByNormalizedName(normalizedName: string): Promise<User | undefined>;
  listAll(options?: { includeInactive?: boolean }): Promise<User[]>;
  save(user: User): Promise<void>;
}

export interface RoleRepository {
  findById(id: RoleId): Promise<Role | undefined>;
  findByKey(key: string): Promise<Role | undefined>;
  listAll(): Promise<Role[]>;
  /** Mapa id → papel, usado na resolução de permissão efetiva. */
  loadAllAsMap(): Promise<Map<RoleId, RoleWithPermissions>>;
  /** Grava o papel e troca a lista de permissões dele inteira. */
  save(role: Role): Promise<void>;
  /** Exclusão física: papel não é registro de negócio, e quem o usava já foi conferido. */
  remove(id: RoleId): Promise<void>;
}

export interface PermissionRepository {
  listAll(): Promise<PermissionDefinition[]>;
}

export interface RoleAssignmentRepository {
  listByUser(userId: UserId): Promise<RoleAssignment[]>;
  find(
    userId: UserId,
    roleId: RoleId,
    projectId: ProjectId | undefined,
  ): Promise<{ id: RoleAssignmentId; assignment: RoleAssignment } | undefined>;
  add(
    id: RoleAssignmentId,
    assignment: RoleAssignment,
    createdAt: string,
    createdByUserId?: UserId,
  ): Promise<void>;
  remove(id: RoleAssignmentId): Promise<void>;
  /**
   * Quantos usuários **ativos** possuem este papel em escopo global.
   * Base da proteção contra ficar sem administrador (RN-136).
   */
  countActiveGlobalHolders(roleId: RoleId): Promise<number>;
  /** Quantas atribuições o papel tem, em qualquer alcance e de qualquer perfil. */
  countByRole(roleId: RoleId): Promise<number>;
  /** As atribuições do papel, de qualquer perfil — quem é o titular do Super administrador. */
  listByRole(roleId: RoleId): Promise<Array<{ id: RoleAssignmentId; assignment: RoleAssignment }>>;
}

export interface AuditLogRepository {
  append(entry: AuditLog): Promise<void>;
  listByEntity(entityType: string, entityId: string): Promise<AuditLog[]>;
  listByProject(projectId: ProjectId, limit: number): Promise<AuditLog[]>;
  listRecent(limit: number): Promise<AuditLog[]>;
}

export interface ProjectRepository {
  findById(id: ProjectId): Promise<Project | undefined>;
  findByNormalizedCode(normalizedCode: string): Promise<Project | undefined>;
  listAll(): Promise<Project[]>;
  listByIds(ids: readonly ProjectId[]): Promise<Project[]>;
  save(project: Project): Promise<void>;
}

export interface WorkCalendarRepository {
  findById(id: WorkCalendarId): Promise<WorkCalendar | undefined>;
  findDefault(): Promise<WorkCalendar | undefined>;
  findByProject(projectId: ProjectId): Promise<WorkCalendar | undefined>;
  listExceptions(calendarId: WorkCalendarId): Promise<WorkCalendarException[]>;
  save(calendar: WorkCalendar): Promise<void>;
  saveException(exception: WorkCalendarException): Promise<void>;
}

/** Preferências e estado da aplicação: usuário ativo, obra ativa, data do último backup. */
export interface SettingsRepository {
  get<T>(key: string): Promise<T | undefined>;
  set<T>(key: string, value: T): Promise<void>;
  remove(key: string): Promise<void>;
}

export const SETTINGS_KEY = {
  /**
   * O segredo da sessão deste aparelho.
   *
   * Fica no dispositivo, como a obra aberta: é o que evita pedir e-mail toda manhã no
   * tablet do canteiro. Quem decide se ele ainda vale é o servidor, ao resolvê-lo — aqui
   * é só onde ele espera.
   */
  SESSION_TOKEN: 'session.token',
  ACTIVE_PROJECT_ID: 'session.activeProjectId',
  LAST_BACKUP_AT: 'backup.lastExportedAt',
  THEME: 'ui.theme',
  PERSISTENCE_REQUESTED: 'storage.persistenceRequested',
  /**
   * Os tokens de quem saiu sem sinal, à espera de o servidor encerrar a sessão (Fase 43,
   * RN-616). Saem daqui quando o servidor responde.
   */
  PENDING_SIGN_OUTS: 'session.pendingSignOuts',
} as const;
